import { findExercise, matchExercises, type ExerciseRow } from '@gymtv/data';
import { useId, useRef, useState, type CSSProperties, type InputHTMLAttributes, type KeyboardEvent } from 'react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & {
  value: string;
  exercises: ExerciseRow[];
  onChange(value: string): void;
  /** The grid's own key handling; used whenever the suggestion list doesn't take the key. */
  onGridKey(e: KeyboardEvent<HTMLInputElement>): void;
};

/**
 * Exercise-name cell with library autocomplete (docs/decisions/0016). While suggestions show,
 * ↓/↑ move through them and Enter/Tab picks one; otherwise keys go to the grid as usual. A name
 * that isn't in the library is tagged "new" and is added to it when the day saves.
 */
export function NameCell({ value, exercises, onChange, onGridKey, ...input }: Props) {
  const listId = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const exact = findExercise(exercises, value);
  const matches = open ? matchExercises(exercises, value).filter((e) => e.name !== value) : [];
  const isNew = value.trim() !== '' && !exact;
  // Fixed, so the grid's horizontal scroller can't clip it.
  const rect = matches.length ? ref.current?.getBoundingClientRect() : undefined;
  const place: CSSProperties | undefined = rect && { left: rect.left, top: rect.bottom + 2, width: Math.max(rect.width, 220) };

  function pick(name: string) {
    onChange(name);
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (matches.length) {
      if (e.key === 'ArrowDown' && !e.altKey) {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, matches.length - 1));
        return;
      }
      if (e.key === 'ArrowUp' && !e.altKey && active >= 0) {
        e.preventDefault();
        setActive((i) => i - 1);
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && active >= 0 && !e.metaKey && !e.ctrlKey) {
        if (e.key === 'Enter') e.preventDefault();
        pick(matches[active]!.name);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
    }
    onGridKey(e);
  }

  return (
    <div className="name-cell">
      <input
        {...input}
        ref={ref}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={matches.length > 0}
        aria-controls={listId}
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      />
      {isNew && !matches.length && (
        <span className="new-tag" title="Not in your exercise library yet: it's added when the day saves">
          new
        </span>
      )}
      {matches.length > 0 && (
        <ul className="suggest" role="listbox" id={listId} style={place}>
          {matches.map((m, i) => (
            <li
              key={m.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'active' : ''}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the cell
                pick(m.name);
              }}
            >
              {m.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
